import { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useQuery, useMutation } from '@tanstack/react-query'
import useApi from '../hooks/useApi'
import useTranslation from '../hooks/useTranslation'
import useLocale from '../hooks/useLocale'
import countryCurrencies from '../data/countryCurrencies.json'

/*
 * The last step of creating a company: the four answers it gives once.
 *
 * All four were previously decided without asking, and each was wrong for
 * somebody. The chart of accounts was the country's default with no say in it.
 * The base currency was derived from the country in the browser and never
 * stored, so the server had no answer at all and a Danish company reporting in
 * EUR could not be expressed. The financial year had nowhere to live, which is
 * why no organization had a single accounting period and the period lock had
 * nothing to lock. And the VAT scheme silently defaulted to `standard`, while
 * that setting is exactly what decides which VAT codes a bookkeeper is shown.
 *
 * It is hand-written rather than a cardViewData entry because none of it is an
 * entity: the chart list comes from a platform endpoint, the periods are
 * generated, and the four answers post together to /accounting/setup. The
 * config system is for records, and this is a conversation.
 *
 * Every answer is pre-filled with the sensible one, so the whole step is a read
 * and a click for most companies — and every answer is editable afterwards, so
 * nothing here is a decision that has to be right the first time.
 */

const Wrap = styled.div`
    max-width: 480px;
`

const Field = styled.div`
    margin-bottom: 1.1rem;
`

const Label = styled.label`
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${p => p.theme.font.label};
    margin-bottom: 0.4rem;
`

const Hint = styled.div`
    font-size: 0.78rem;
    color: ${p => p.theme.font.label};
    margin-top: 0.3rem;
`

const controlStyle = ({ theme }) => `
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: 1px solid ${theme.form.border};
    border-radius: 0.375rem;
    background: ${theme.form.background};
    color: ${theme.font.color};
    font-family: "Euclid Circular B", sans-serif;
    font-size: 0.9rem;
`

const Select = styled.select`${controlStyle}`
const Input = styled.input`${controlStyle}`

const Row = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
`

const Button = styled.button`
    padding: 0.55rem 1.3rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    background: ${p => p.$variant === 'secondary' ? 'transparent' : p.theme.btn.primary};
    color: ${p => p.$variant === 'secondary' ? p.theme.font.label : p.theme.btn.font};
    border: ${p => p.$variant === 'secondary' ? `1px solid ${p.theme.form.border}` : 'none'};

    &:disabled { opacity: 0.5; cursor: default; }
    &:not(:disabled):hover { opacity: 0.88; }
`

const ErrorText = styled.div`
    color: #c0392b;
    font-size: 0.85rem;
    margin-top: 0.75rem;
`

/*
 * The schemes accountingSettings.vatProfile offers, in the order they widen:
 * domestic, then domestic with exempt sales, then the two that add EU and
 * non-EU, then the special schemes. The list is short and fixed by the schema's
 * own enum, so it is spelled out rather than fetched.
 *
 * `standard` is the one selected, and it is marked as such rather than moved to
 * the top: almost every company sells across a border eventually, and the cost
 * of offering a few VAT codes too many is far lower than hiding the one the
 * bookkeeper needs. It is also what the schema defaults to server-side, so the
 * two cannot disagree.
 */
const DEFAULT_VAT_PROFILE = 'standard'

const VAT_PROFILES = ['domestic', 'domesticMixed', 'standard', 'standardMixed', 'extended']

const CreateOrgAccounting = ({ country, onDone }) => {
    const { t } = useTranslation()
    const { currentOrgLang } = useLocale()
    const { getMe, postMe } = useApi()

    const [templateId, setTemplateId] = useState('')
    const [currency, setCurrency] = useState(() => countryCurrencies[country]?.currency || '')
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(1)
    const [vatProfile, setVatProfile] = useState(DEFAULT_VAT_PROFILE)
    const [vatNumber, setVatNumber] = useState('')

    const { data: listData, isLoading: templatesLoading } = useQuery({
        queryKey: ['coaTemplate', 'list'],
        queryFn: () => getMe('coaTemplate/list'),
    })
    const templates = Array.isArray(listData?.data) ? listData.data : []

    /*
     * The country's own chart, pre-selected, so the common case is a read and a
     * click. Only while nothing has been chosen.
     *
     * The default chart also settles the currency when the profile cannot: this
     * step runs seconds after the org was created and switched into, and the
     * org list the profile reads from can still be the old one. The template
     * list is server-side and org-scoped, so the chart it calls default is the
     * most reliable thing on screen about which country this company is in.
     */
    useEffect(() => {
        const preferred = templates.find(tpl => tpl.isDefault)
        if (!preferred) return
        setTemplateId(current => current || preferred.id)
        setCurrency(current => current || countryCurrencies[preferred.country]?.currency || '')
    }, [listData])

    /*
     * The twelve months, named by Intl rather than by twelve translation keys —
     * that is eleven languages of month names the app would otherwise have to
     * carry and keep in step, for something every platform already knows.
     *
     * Capitalised, because Intl gives the form a month takes in a sentence and
     * Danish, German, Spanish and French all write it lowercase there: on its
     * own in a dropdown, "januar" reads like a mistake. `toLocaleUpperCase`
     * rather than `toUpperCase` so a Turkish i keeps its dot, and the spread
     * takes a whole character rather than half a surrogate pair.
     */
    const months = useMemo(() => {
        const format = new Intl.DateTimeFormat(currentOrgLang, { month: 'long', timeZone: 'UTC' })
        const capitalise = (name) => {
            const [first, ...rest] = [...name]
            return first ? first.toLocaleUpperCase(currentOrgLang) + rest.join('') : name
        }
        return Array.from({ length: 12 }, (_, i) => ({
            value: i + 1,
            label: capitalise(format.format(new Date(Date.UTC(2000, i, 1))))
        }))
    }, [currentOrgLang])

    const setup = useMutation({
        mutationFn: () => postMe('accounting/setup', {
            coaTemplateId: templateId,
            currency,
            currencyName: currency,
            fiscalYearStartMonth: Number(fiscalYearStartMonth),
            vatProfile,
            vatNumber: vatNumber.trim() || null
        }),
        onSuccess: (result) => {
            if (result?.error) return
            onDone()
        }
    })

    return (
        <Wrap data-testid='createOrgAccounting'>
            <Field>
                <Label htmlFor='coaTemplate'>{t('createOrg.form.accounting.chart')}</Label>
                {/* The list is fetched, so it is briefly empty. An empty select
                    reads as "there are no charts" rather than "not yet", which
                    is the difference between waiting and giving up. */}
                <Select
                    id='coaTemplate'
                    data-testid='createOrgAccounting.coaTemplateId'
                    value={templateId}
                    disabled={templates.length === 0}
                    onChange={(e) => setTemplateId(e.target.value)}
                >
                    {templates.length === 0 && (
                        <option value=''>
                            {templatesLoading ? t('createOrg.form.accounting.chart.loading') : t('createOrg.form.accounting.chart.none')}
                        </option>
                    )}
                    {templates.map(tpl => (
                        <option key={tpl.id} value={tpl.id}>
                            {tpl.name} ({tpl.country}){tpl.isDefault ? ` — ${t('createOrg.form.accounting.recommended')}` : ''}
                        </option>
                    ))}
                </Select>
                <Hint>{t('createOrg.form.accounting.chart.hint')}</Hint>
            </Field>

            <Field>
                <Label htmlFor='currency'>{t('createOrg.form.accounting.currency')}</Label>
                <Input
                    id='currency'
                    data-testid='createOrgAccounting.currency'
                    value={currency}
                    maxLength={3}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
                <Hint>{t('createOrg.form.accounting.currency.hint')}</Hint>
            </Field>

            <Field>
                <Label htmlFor='fiscalYearStartMonth'>{t('createOrg.form.accounting.fiscalYear')}</Label>
                <Select
                    id='fiscalYearStartMonth'
                    data-testid='createOrgAccounting.fiscalYearStartMonth'
                    value={fiscalYearStartMonth}
                    onChange={(e) => setFiscalYearStartMonth(e.target.value)}
                >
                    {months.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                </Select>
                <Hint>{t('createOrg.form.accounting.fiscalYear.hint')}</Hint>
            </Field>

            <Field>
                <Label htmlFor='vatProfile'>{t('createOrg.form.accounting.vatProfile')}</Label>
                <Select
                    id='vatProfile'
                    data-testid='createOrgAccounting.vatProfile'
                    value={vatProfile}
                    onChange={(e) => setVatProfile(e.target.value)}
                >
                    {VAT_PROFILES.map(profile => (
                        <option key={profile} value={profile}>
                            {t(`createOrg.form.accounting.vatProfile.${profile}`)}
                            {profile === DEFAULT_VAT_PROFILE ? ` — ${t('createOrg.form.accounting.recommended')}` : ''}
                        </option>
                    ))}
                </Select>
                <Hint>{t('createOrg.form.accounting.vatProfile.hint')}</Hint>
            </Field>

            <Field>
                <Label htmlFor='vatNumber'>{t('createOrg.form.accounting.vatNumber')}</Label>
                <Input
                    id='vatNumber'
                    data-testid='createOrgAccounting.vatNumber'
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    placeholder={t('createOrg.form.accounting.vatNumber.placeholder')}
                />
            </Field>

            <Row>
                <Button
                    data-testid='createOrgAccounting.saveNContinue'
                    disabled={setup.isPending}
                    onClick={() => setup.mutate()}
                >
                    {setup.isPending ? t('createOrg.form.accounting.saving') : t('createOrg.form.accounting.finish')}
                </Button>
                {/* Skipping is allowed on purpose: the country's chart is already
                    cloned and every answer here has a working default, so a
                    company in a hurry is not blocked — and Accounting Settings
                    can set all of it afterwards. */}
                <Button
                    $variant='secondary'
                    data-testid='createOrgAccounting.skip'
                    disabled={setup.isPending}
                    onClick={onDone}
                >
                    {t('createOrg.form.accounting.skip')}
                </Button>
            </Row>

            {setup.data?.error && <ErrorText>{t(setup.data.error)}</ErrorText>}
        </Wrap>
    )
}

export default CreateOrgAccounting
